import { useRef, type ComponentType, type ReactNode } from 'react';

/**
 * The product's three ways of asking a reader to choose, and nothing else.
 *
 * Before this file there were five: the schedule and the fleet drew a chip
 * inside a frame, the users page and the provider console filled the selected
 * choice with `button--primary`, and the register underlined it. One question
 * -- "which of these am I looking at?" -- answered a different way on each
 * screen, so a reader who learned one page learned nothing about the next.
 *
 * Harmonizing them meant deciding what the *shape* of a control is allowed to
 * promise, and then never breaking that promise:
 *
 *   `Tabs`        chips in one frame   exactly one; the panel below changes.
 *   `ChoiceGroup` chips in one frame   exactly one; nothing navigates -- a
 *                                      mode, a unit, a drawing tool.
 *   `ToggleGroup` loose pills          any number, including none. A filter.
 *
 * The frame carries the whole distinction: things sharing one box are one dial
 * with one position. That is why the schedule's status filters could not stay
 * in a `segmented` -- multi-select inside a frame is a control lying about
 * itself. Loose pills say "several independent switches", which is what they
 * are.
 *
 * None of the three fills the selected item with the primary colour. That fill
 * means "the main action on this page" and is usually sitting two controls
 * along the same row; one fill, one meaning. Selection is carried by a
 * background change, a weight change and the ARIA state -- never by colour
 * alone (section 18).
 */

export type SelectionItem<T extends string> = {
  value: T;
  /** Always present, even when an icon is shown: the icon never stands alone. */
  label: string;
  /**
   * Optional pictogram. Carrying one is what earns a control the right to drop
   * its label on a narrow window, because the shape still says which is which.
   */
  icon?: ComponentType;
};

/**
 * Arrow-key traversal, which is the part every one of the five hand-rolled
 * implementations was missing -- including the three that announced themselves
 * as a `tablist` and therefore promised it.
 *
 * Returns the index focus should move to, or null for a key we do not handle.
 * Every one of these rows is horizontal, and Home and End jump to the ends, as
 * ARIA's authoring practices require.
 */
export function nextIndexForKey(key: string, current: number, count: number): number | null {
  if (key === 'ArrowRight' || key === 'ArrowDown') return (current + 1) % count;
  if (key === 'ArrowLeft' || key === 'ArrowUp') return (current - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

/**
 * Roving tabindex: the group is one stop on the page's tab order and the arrow
 * keys move within it. A row of six register kinds must not cost six tab
 * presses to step over on the way to the list underneath.
 */
function useRovingFocus() {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const focus = (index: number) => refs.current[index]?.focus();
  const register = (index: number) => (element: HTMLButtonElement | null) => {
    refs.current[index] = element;
  };
  return { focus, register };
}

/**
 * A row of tabs over a `TabPanel`.
 *
 * Activation is manual -- the arrows move focus, Enter or Space commits.
 * ARIA's authoring practices prefer selection to follow focus, but only "as
 * long as their associated tab panels are displayed without noticeable
 * latency", and none of ours qualify: every panel here either fetches a page
 * of rows or rewrites the query string. Arrowing from the first register kind
 * to the sixth should not fire five list requests on the way past.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  /** Namespaces the generated ids, so two tab rows can share one page. */
  idPrefix,
  /** For rows long enough to need a second line -- the register's six kinds. */
  wrap = false,
}: {
  items: ReadonlyArray<SelectionItem<T>>;
  value: T;
  onChange: (value: T) => void;
  label: string;
  idPrefix: string;
  wrap?: boolean;
}) {
  const { focus, register } = useRovingFocus();

  return (
    <div
      className={`segmented${wrap ? ' segmented--wrap' : ''}`}
      role="tablist"
      aria-label={label}
      aria-orientation="horizontal"
    >
      {items.map((item, index) => {
        const Icon = item.icon;
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={register(index)}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${item.value}`}
            className="segmented__item"
            aria-selected={selected}
            /* Only the selected tab names a panel. The other panels are not
               rendered, and `aria-controls` pointing at an absent element is
               worse than saying nothing at all. */
            aria-controls={selected ? `${idPrefix}-panel-${item.value}` : undefined}
            /* An explicit name survives the narrow-window rule that hides the
               text beside an icon, so an icon-only segment still has one. */
            aria-label={Icon ? item.label : undefined}
            tabIndex={selected ? 0 : -1}
            onKeyDown={(event) => {
              const next = nextIndexForKey(event.key, index, items.length);
              if (next === null) return;
              event.preventDefault();
              focus(next);
            }}
            onClick={() => onChange(item.value)}
          >
            {Icon ? <Icon /> : null}
            <span className="segmented__label">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The panel a `Tabs` row controls.
 *
 * Separate from `Tabs` because every page puts something between the two -- a
 * toolbar, a filter row, a banner -- and a component owning both would have had
 * to invent a slot for it.
 */
export function TabPanel({
  idPrefix,
  value,
  children,
  /**
   * For a panel that is also something else -- the schedule's views live in a
   * `card` that is a child of the workspace grid, and wrapping it in a second
   * div to hold the role would have put a layer between the grid and its item.
   */
  className,
}: {
  idPrefix: string;
  value: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      id={`${idPrefix}-panel-${value}`}
      role="tabpanel"
      aria-labelledby={`${idPrefix}-tab-${value}`}
      /* Focusable so a keyboard reader who commits a tab steps straight into
         what it revealed, instead of tabbing back out through the row. */
      tabIndex={0}
      className={className ? `tab-panel ${className}` : 'tab-panel'}
    >
      {children}
    </div>
  );
}

/**
 * Exactly one of several, where choosing does not navigate: a drawing tool, a
 * mode, a unit.
 *
 * Radios rather than tabs, because nothing is being revealed -- and radios are
 * what permit selection to follow focus, which is right here for the same
 * reason it is wrong on `Tabs`: choosing costs nothing.
 */
export function ChoiceGroup<T extends string>({
  items,
  value,
  onChange,
  label,
  wrap = false,
}: {
  items: ReadonlyArray<SelectionItem<T>>;
  value: T;
  onChange: (value: T) => void;
  label: string;
  wrap?: boolean;
}) {
  const { focus, register } = useRovingFocus();
  const selectedIndex = items.findIndex((item) => item.value === value);

  return (
    <div
      className={`segmented${wrap ? ' segmented--wrap' : ''}`}
      role="radiogroup"
      aria-label={label}
    >
      {items.map((item, index) => {
        const Icon = item.icon;
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={register(index)}
            type="button"
            role="radio"
            className="segmented__item"
            aria-checked={selected}
            aria-label={Icon ? item.label : undefined}
            /* A radio group with nothing chosen still needs one tab stop, or
               the keyboard cannot reach it at all. */
            tabIndex={selected || (selectedIndex === -1 && index === 0) ? 0 : -1}
            onKeyDown={(event) => {
              const next = nextIndexForKey(event.key, index, items.length);
              if (next === null) return;
              event.preventDefault();
              focus(next);
              const target = items[next];
              if (target) onChange(target.value);
            }}
            onClick={() => onChange(item.value)}
          >
            {Icon ? <Icon /> : null}
            <span className="segmented__label">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Any number of several, including none: a filter.
 *
 * Drawn as loose pills rather than chips in a frame, because a frame promises
 * one position and this control has as many positions as it has pills. Each
 * pill is its own tab stop -- they are independent switches, so a roving
 * tabindex that let the keyboard step past the group would be skipping real
 * controls rather than saving presses.
 */
export function ToggleGroup<T extends string>({
  items,
  values,
  onToggle,
  label,
}: {
  items: ReadonlyArray<SelectionItem<T>>;
  values: ReadonlyArray<T>;
  onToggle: (value: T) => void;
  label: string;
}) {
  return (
    <div className="chip-group" role="group" aria-label={label}>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.value}
            type="button"
            className="chip"
            aria-pressed={values.includes(item.value)}
            onClick={() => onToggle(item.value)}
          >
            {Icon ? <Icon /> : null}
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}
